import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import type { PatientDetail } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { RESOURCE, routes } from '#client/lib/routes';
import { type TrendTest, groupReportsByTest, pickActiveTest } from './patient-trends.logic';
import { TrendComparison } from './TrendComparison';
import { useReportHistory } from './useReportHistory';
import { ButtonLink } from '#client/components/ui/ButtonLink';

const VISIT_COUNTS = [
  { value: '5', label: 'Last 5' },
  { value: '10', label: 'Last 10' },
  { value: '12', label: 'All' },
];

/**
 * Every visit of one test, side by side (requirement 9).
 *
 * Per-patient with a test selector rather than per-report: one table across two
 * different lab tests has no shared rows, so there would be nothing to compare.
 * A test the patient has had only once is listed and marked, rather than hidden
 * — "we have done this once" is an answer to the question being asked.
 */
export function PatientTrendsPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const [limit, setLimit] = useState('10');

  const patient = useResourceItem<PatientDetail>(RESOURCE.patients, id);

  // Newest report per test, which is the anchor the history query wants. The
  // patient payload already carries every report, so this costs no request.
  const tests = useMemo(() => groupReportsByTest(patient.data?.reports ?? []), [patient.data]);

  const active = pickActiveTest(tests, params.get('test'));
  const history = useReportHistory(active?.report_id, Number(limit));

  if (patient.loading) return <PageLoading title="Compare results" />;
  if (!patient.data) {
    return <PageMessage title="Patient not found">{patient.error}</PageMessage>;
  }

  return (
    <Page
      title={`${patient.data.name} — results over time`}
      subtitle="Every visit of one test, side by side"
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.patient(patient.data.id)}>
          Back to patient
        </ButtonLink>
      }
      fullWidth
    >
      {tests.length === 0 ? (
        <EmptyState title="No reports yet" body="Nothing has been booked for this patient." />
      ) : (
        <div className="c-form-stack">
          <TestPicker
            tests={tests}
            active={active}
            limit={limit}
            onTestChange={(choice) => setParams({ test: choice })}
            onLimitChange={setLimit}
          />

          {history.loading && <Card title="Loading">…</Card>}
          {history.data && <TrendComparison history={history.data} />}
        </div>
      )}
    </Page>
  );
}

function TestPicker({
  tests,
  active,
  limit,
  onTestChange,
  onLimitChange,
}: {
  tests: readonly TrendTest[];
  active: TrendTest | undefined;
  limit: string;
  onTestChange: (testId: string) => void;
  onLimitChange: (limit: string) => void;
}) {
  return (
    <Card title="Which test">
      <div className="c-form-grid">
        <Select
          label="Test"
          options={tests.map((test) => ({
            value: String(test.test_id),
            label: test.name,
          }))}
          value={String(active?.test_id ?? '')}
          onChange={onTestChange}
        />
        <Segmented
          label="Visits shown"
          options={VISIT_COUNTS}
          value={limit}
          onChange={onLimitChange}
        />
      </div>
    </Card>
  );
}
